/**
 * Web Storage - MMKV interface implementation using localStorage
 *
 * This module provides a storage interface compatible with mobile's MMKV API
 * Uses localStorage for synchronous access (like MMKV)
 * IndexedDB is used for Legend-State persistence separately
 */

/**
 * WebStorage interface - MMKV compatible API for web
 */
export interface WebStorage {
    getString(key: string): string | null;
    setString(key: string, value: string): void;
    getNumber(key: string): number | null;
    setNumber(key: string, value: number): void;
    getBoolean(key: string): boolean | null;
    setBoolean(key: string, value: boolean): void;
    getObject<T>(key: string): T | null;
    setObject<T>(key: string, value: T): void;
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    delete(key: string): void;
    removeItem(key: string): void;
    getAllKeys(): string[];
    contains(key: string): boolean;
    clearAll(): void;
}

const PREFIX = 'kidsplaza_';

/**
 * Web storage implementation using localStorage
 * Provides MMKV-compatible synchronous interface for web browsers
 */
export const webStorage: WebStorage = {
    getString(key: string): string | null {
        if (typeof window === 'undefined') return null;
        return localStorage.getItem(PREFIX + key);
    },

    setString(key: string, value: string): void {
        if (typeof window === 'undefined') return;
        localStorage.setItem(PREFIX + key, value);
    },

    getNumber(key: string): number | null {
        const value = this.getString(key);
        return value !== null ? Number(value) : null;
    },

    setNumber(key: string, value: number): void {
        this.setString(key, String(value));
    },

    getBoolean(key: string): boolean | null {
        const value = this.getString(key);
        if (value === null) return null;
        return value === 'true';
    },

    setBoolean(key: string, value: boolean): void {
        this.setString(key, String(value));
    },

    getObject<T>(key: string): T | null {
        const value = this.getString(key);
        if (value === null) return null;
        try {
            return JSON.parse(value) as T;
        } catch {
            return null;
        }
    },

    setObject<T>(key: string, value: T): void {
        this.setString(key, JSON.stringify(value));
    },

    getItem(key: string): string | null {
        return this.getString(key);
    },

    setItem(key: string, value: string): void {
        this.setString(key, value);
    },

    delete(key: string): void {
        if (typeof window === 'undefined') return;
        localStorage.removeItem(PREFIX + key);
    },

    removeItem(key: string): void {
        this.delete(key);
    },

    getAllKeys(): string[] {
        if (typeof window === 'undefined') return [];
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(PREFIX)) {
                keys.push(key.substring(PREFIX.length));
            }
        }
        return keys;
    },

    contains(key: string): boolean {
        return this.getString(key) !== null;
    },

    clearAll(): void {
        if (typeof window === 'undefined') return;
        const keysToRemove = this.getAllKeys();
        keysToRemove.forEach(key => this.delete(key));
    },
};

/**
 * Alias for webStorage - matches mobile's storage export
 */
export const storage = webStorage;

export default webStorage;
